import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Icon } from '../../../design-system/icons';
import { ActionsMenu, type MenuAction } from '../../../design-system/primitives';
import type { FolderNode } from './folder-source';

/**
 * Habillage d'une liste à dossiers (ADR-149 §4, §5) : fil d'Ariane de la page, menu « Nouveau » et
 * raccourcis de la présentation drive, arborescence repliable (présentation tree, et colonne du
 * drive), puces de filtre, bascule d'affichage, portée de recherche. Présentation seulement : chaque
 * geste devient une commande déclarée, autorisée au serveur.
 */

/** Cible de dépôt d'un glisser-déposer interne : tout élément qui reçoit des éléments déplacés. */
export type DropTarget = { onDragOver: (event: DragEvent<HTMLElement>) => void; onDragLeave: (event: DragEvent<HTMLElement>) => void; onDrop: (event: DragEvent<HTMLElement>) => void; 'data-drop-over'?: boolean };

export interface TrailStep {
  label: string;
  onSelect?: () => void;
}

/**
 * Fil d'Ariane de la page (charpente commune, ADR-149 addendum) : les étapes de la page (Accueil,
 * module), la racine de la source puis le chemin du dossier ; chaque maillon ouvre, et reçoit des
 * éléments glissés (rangement). Le dernier maillon est la page courante.
 */
export function FolderBreadcrumb({ trail, rootLabel, path, title, onOpen, dropTarget }: { trail: readonly TrailStep[]; rootLabel: string; path: ReadonlyArray<{ id: string; name: string }>; title?: string; onOpen: (folderId: string | null) => void; dropTarget: (folderId: string | null) => DropTarget }) {
  const crumbs = title !== undefined ? [] : path;
  const last = title ?? (crumbs.length === 0 ? rootLabel : crumbs[crumbs.length - 1]!.name);
  const before: Array<{ id: string | null; name: string }> = title !== undefined || crumbs.length > 0 ? [{ id: null, name: rootLabel }, ...crumbs.slice(0, -1)] : [];
  const link = (label: string, onSelect: () => void) => (
    <a href="#" onClick={(event) => { event.preventDefault(); onSelect(); }}>
      {label}
    </a>
  );
  return (
    <nav className="ay-breadcrumb ay-folders__crumbs" aria-label="Fil d’Ariane">
      <ol>
        {trail.map((step) => (
          <li key={step.label}>{step.onSelect === undefined ? <span>{step.label}</span> : link(step.label, step.onSelect)}</li>
        ))}
        {before.map((crumb) => (
          <li key={crumb.id ?? 'root'} {...dropTarget(crumb.id)}>
            {link(crumb.name, () => onOpen(crumb.id))}
          </li>
        ))}
        <li>
          <span aria-current="page">{last}</span>
        </li>
      </ol>
    </nav>
  );
}

function useOutside(active: boolean, ref: { current: HTMLElement | null }, close: () => void): void {
  const latest = useRef(close);
  latest.current = close;
  useEffect(() => {
    if (!active) return;
    const onDown = (event: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) latest.current();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') latest.current();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [active, ref]);
}

/** Bouton « Nouveau » de la présentation drive et son menu. */
export function NewMenu({ entries }: { entries: ReadonlyArray<{ id: string; label: string; icon: string; onSelect: () => void; separator?: boolean; disabled?: boolean; reason?: string }> }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useOutside(open, root, () => setOpen(false));
  if (entries.length === 0) return null;
  // Aucune entrée permise : le bouton lui-même est grisé, le motif dit (refus visible d'avance).
  const reason = entries.every((entry) => entry.disabled === true) ? entries[0]!.reason : undefined;
  return (
    <div className="ay-drive__new" ref={root}>
      <button type="button" className="ay-drive__new-button" aria-haspopup="menu" aria-expanded={open} aria-disabled={reason === undefined ? undefined : true} title={reason} onClick={() => reason === undefined && setOpen((value) => !value)}>
        <Icon name="plus" size={20} /> Nouveau
        {reason !== undefined && <span className="ay-visually-hidden"> — {reason}</span>}
      </button>
      {open && (
        <ul className="ay-folders__menu ay-drive__new-menu" role="menu" aria-label="Nouveau">
          {entries.map((entry) => (
            <li key={entry.id} role="none" className={entry.separator === true ? 'ay-folders__menu-separator' : undefined}>
              <button type="button" role="menuitem" aria-disabled={entry.disabled === true ? true : undefined} title={entry.reason} onClick={() => { if (entry.disabled === true) return; setOpen(false); entry.onSelect(); }}>
                <Icon name={entry.icon} size={15} /> {entry.label}
                {entry.disabled === true && entry.reason !== undefined && <span className="ay-folders__menu-hint">{entry.reason}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Raccourcis à gauche : des filtres gouvernés nommés, un tri, une portée, ou un autre espace de travail. */
export function Shortcuts({ items, current, onPick }: { items: ReadonlyArray<{ id: string; label: string; icon?: string }>; current: string | null; onPick: (id: string) => void }) {
  return (
    <nav className="ay-drive__shortcuts" aria-label="Raccourcis">
      {items.map((item) => (
        <button key={item.id} type="button" className="ay-drive__shortcut" aria-current={current === item.id ? 'page' : undefined} onClick={() => onPick(item.id)}>
          <Icon name={item.icon ?? 'folder'} size={17} />
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

/**
 * Arborescence des dossiers (présentation tree, et colonne de gauche du drive comme « Mon Drive ») :
 * `role="tree"`, repliable (chevron, flèches gauche / droite), dossier courant surligné, compteur quand
 * il est connu, Entrée ou clic ouvre ; chaque nœud reçoit des éléments glissés, porte un menu ⋮ et un
 * « + » de sous-dossier, avec les mêmes refus visibles d'avance que la liste.
 */
export function FolderTree({
  folders,
  current,
  rootLabel,
  onOpen,
  dropTarget,
  onCreate,
  createDenied,
  hideRoot = false,
  count,
  menu,
  onCreateIn,
}: {
  folders: readonly FolderNode[] | null;
  current: string | null;
  rootLabel: string;
  onOpen: (folderId: string | null) => void;
  dropTarget: (folderId: string | null) => DropTarget;
  onCreate?: () => void;
  createDenied?: string;
  /** Racine déjà présente (raccourci « Tous les documents » du drive) : pas de nœud racine. */
  hideRoot?: boolean;
  count?: (folderId: string) => number | undefined;
  menu?: (folder: FolderNode) => MenuAction[];
  onCreateIn?: (folder: FolderNode) => void;
}) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const children = new Map<string, FolderNode[]>();
  const known = new Set((folders ?? []).map((folder) => folder.id));
  for (const folder of folders ?? []) {
    const parent = folder.parent_id !== null && known.has(folder.parent_id) ? folder.parent_id : '';
    children.set(parent, [...(children.get(parent) ?? []), folder]);
  }
  // Le chemin du dossier courant est déplié d'office.
  const byId = new Map((folders ?? []).map((folder) => [folder.id, folder]));
  const expanded = new Set(open);
  for (let at = current === null ? undefined : byId.get(current)?.parent_id, depth = 0; at !== undefined && at !== null && depth < 32; at = byId.get(at)?.parent_id, depth += 1) expanded.add(at);
  const toggle = (id: string): void => setOpen((value) => {
    const next = new Set(value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const node = (folder: FolderNode, level: number): ReactNode => {
    const kids = [...(children.get(folder.id) ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    const isOpen = expanded.has(folder.id);
    const counted = count?.(folder.id);
    return (
      <li key={folder.id} role="none">
        <div
          role="treeitem"
          tabIndex={folder.id === current ? 0 : -1}
          aria-level={level}
          aria-selected={folder.id === current}
          {...(kids.length > 0 ? { 'aria-expanded': isOpen } : {})}
          className="ay-tree__item"
          style={{ paddingLeft: 6 + (level - 1) * 16 }}
          onClick={() => onOpen(folder.id)}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === 'Enter') onOpen(folder.id);
            else if (event.key === 'ArrowRight' && kids.length > 0 && !isOpen) toggle(folder.id);
            else if (event.key === 'ArrowLeft' && isOpen) toggle(folder.id);
          }}
          {...dropTarget(folder.id)}
        >
          <button type="button" className="ay-tree__chevron" tabIndex={-1} aria-label={kids.length === 0 ? undefined : isOpen ? `Replier ${folder.name}` : `Déplier ${folder.name}`} aria-hidden={kids.length === 0 ? true : undefined} onClick={(event) => { event.stopPropagation(); toggle(folder.id); }}>
            {kids.length > 0 ? <Icon name={isOpen ? 'chevron-down' : 'chevron'} size={12} /> : null}
          </button>
          <Icon name="folder" size={16} />
          <span className="ay-tree__name">{folder.name}</span>
          {folder.locked && <Icon name="lock" size={12} title="Dossier système" />}
          {counted !== undefined && <span className="ay-tree__count">{counted.toLocaleString('fr')}</span>}
          {(menu !== undefined || onCreateIn !== undefined) && (
            <span className="ay-tree__tools" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
              {onCreateIn !== undefined && (
                <button type="button" className="ay-tree__add" aria-label={createDenied === undefined ? `Nouveau sous-dossier dans ${folder.name}` : `Nouveau sous-dossier dans ${folder.name} — ${createDenied}`} aria-disabled={createDenied === undefined ? undefined : true} title={createDenied ?? 'Nouveau sous-dossier'} onClick={() => createDenied === undefined && onCreateIn(folder)}>
                  <Icon name="plus" size={13} />
                </button>
              )}
              {menu !== undefined && <ActionsMenu label={`Menu du dossier ${folder.name} dans l’arborescence`} actions={menu(folder)} />}
            </span>
          )}
        </div>
        {isOpen && kids.length > 0 && <ul role="group">{kids.map((kid) => node(kid, level + 1))}</ul>}
      </li>
    );
  };
  const roots = [...(children.get('') ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  return (
    <aside className={`ay-tree${hideRoot ? ' ay-tree--embedded' : ''}`} aria-label="Dossiers">
      <div className="ay-tree__head">
        <strong>Dossiers</strong>
        {onCreate !== undefined && (
          <button type="button" className="ay-tree__add" aria-label={createDenied === undefined ? 'Nouveau dossier' : `Nouveau dossier — ${createDenied}`} aria-disabled={createDenied === undefined ? undefined : true} title={createDenied ?? 'Nouveau dossier'} onClick={() => createDenied === undefined && onCreate()}>
            <Icon name="plus" size={14} />
          </button>
        )}
      </div>
      <ul role="tree" aria-label="Arborescence des dossiers">
        {!hideRoot && (
          <li role="none">
            <div role="treeitem" tabIndex={current === null ? 0 : -1} aria-level={1} aria-selected={current === null} className="ay-tree__item ay-tree__item--root" onClick={() => onOpen(null)} onKeyDown={(event) => event.key === 'Enter' && onOpen(null)} {...dropTarget(null)}>
              <Icon name="home" size={16} />
              <span className="ay-tree__name">{rootLabel}</span>
            </div>
          </li>
        )}
        {roots.map((folder) => node(folder, 1))}
      </ul>
      {!hideRoot && <p className="ay-tree__hint">Glissez des éléments sur un dossier pour les y ranger.</p>}
    </aside>
  );
}

/** Puce de filtre à menu : « Type ▾ », « Étiquettes ▾ »… ; la valeur choisie s'affiche dans la puce. */
export function ChipMenu({ label, options, selected, onPick }: { label: string; options: ReadonlyArray<{ id: string; label: string }>; selected: string; onPick: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  useOutside(open, root, () => setOpen(false));
  const chosen = options.find((option) => option.id === selected && selected !== '');
  return (
    <span className="ay-folders__chip-wrap" ref={root}>
      <button type="button" className={`ay-folders__chip${chosen === undefined ? '' : ' ay-folders__chip--on'}`} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        {chosen === undefined ? label : <>{label} : <strong>{chosen.label}</strong></>} <Icon name="chevron-down" size={13} />
      </button>
      {open && (
        <div className="ay-folders__menu ay-folders__menu--list" role="listbox" aria-label={label}>
          {options.length === 0 && <span className="ay-folders__menu-empty">Aucune valeur.</span>}
          {[{ id: '', label: 'Tous' }, ...options].map((option) => (
            <button key={option.id || 'all'} type="button" role="option" aria-selected={option.id === selected} onClick={() => { setOpen(false); onPick(option.id); }}>
              {option.id === selected ? <Icon name="check" size={14} /> : <span className="ay-folders__menu-gap" />} {option.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/** Bascule d'affichage (radiogroup) : tableau, grille ; à place fixe. */
export function ViewSwitch({ modes, current, onPick }: { modes: ReadonlyArray<{ id: string; label: string; icon: string }>; current: string; onPick: (id: string) => void }) {
  if (modes.length < 2) return null;
  return (
    <div className="ay-folders__switch" role="radiogroup" aria-label="Affichage">
      {modes.map((mode) => (
        <button key={mode.id} type="button" role="radio" aria-checked={mode.id === current} aria-label={mode.label} title={mode.label} data-mode-icon={mode.icon} onClick={() => onPick(mode.id)}>
          <Icon name={mode.icon} size={15} />
          <span className="ay-folders__switch-label">{mode.label}</span>
        </button>
      ))}
    </div>
  );
}

